<template>
  <div>    
    <b-card>
      <b-row class="mb-1" align-v="center">
        <b-col md="3">
          
        </b-col>
        <b-col md="3">

          <b-form-group
            label="Building"
            label-for="building"
          >
            <v-select
              label="name"
              v-model="building"
              placeholder="Select Building"
              :options="buildings"
              autoscroll
              :clearable="true"
            ></v-select>
          </b-form-group>
        </b-col>
        <b-col md="3" class="text-right">
          <b-button variant="primary" pill @click="search">
            <feather-icon icon="SearchIcon" class="mr-50" />
            <span class="align-middle">Search</span>
          </b-button>&nbsp;
          
          <b-button variant="success" pill @click="exportToCSV">
            <feather-icon icon="DownloadIcon" class="mr-50" />
            <span class="align-middle">Export CSV</span>
          </b-button>
        </b-col>
      </b-row>

      <b-row class="mb-1" align-v="center">
        <b-col md="3">
          
        </b-col>
        <b-col md="3">
          <h1>{{ building.name }}</h1>          
        </b-col>
        <b-col md="3" class="text-right">
          
        </b-col>
      </b-row>
      

      <div class="svg-container">
        <svg
          id="buildingSvg"
          ref="svg"
          viewBox="0 0 1800 2100"
          xmlns="http://www.w3.org/2000/svg"
          preserveAspectRatio="xMidYMid meet"
        >
          <defs>
            <!-- Gradients -->
            <linearGradient id="glassGrad" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stop-color="#3a5670"/>
              <stop offset="30%" stop-color="#2a4a66"/>
              <stop offset="70%" stop-color="#1e3a52"/>
              <stop offset="100%" stop-color="#152a3a"/>
            </linearGradient>
            <linearGradient id="windowGrad" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stop-color="#1e4055"/>
              <stop offset="100%" stop-color="#0f2533"/>
            </linearGradient>
            <linearGradient id="highlightGrad" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stop-color="#00ddff" stop-opacity="0.4"/>
              <stop offset="50%" stop-color="#00ddff" stop-opacity="0.15"/>
              <stop offset="100%" stop-color="#00ddff" stop-opacity="0"/>
            </linearGradient>
            <linearGradient id="entranceGlow" x1="50%" y1="0%" x2="50%" y2="100%">
              <stop offset="0%" stop-color="#00eeff" stop-opacity="0.7"/>
              <stop offset="80%" stop-color="#0099cc" stop-opacity="0.2"/>
              <stop offset="100%" stop-color="#006688" stop-opacity="0"/>
            </linearGradient>

            <!-- Filters -->
            <filter id="labelShadow">
              <feDropShadow dx="0" dy="4" stdDeviation="6" flood-color="#000000" flood-opacity="0.6"/>
            </filter>
          </defs>

          <!-- Twinkling stars -->
          <g id="stars" opacity="0.6">
            <circle cx="200" cy="150" r="1.5" fill="#ffffff">
              <animate attributeName="opacity" values="0.4;0.8;0.4" dur="4s" repeatCount="indefinite"/>
            </circle>
            <circle cx="1600" cy="100" r="1.2" fill="#a0c4ff">
              <animate attributeName="opacity" values="0.4;0.7;0.4" dur="5s" repeatCount="indefinite"/>
            </circle>
          </g>

          <!-- Building Group (tower only) -->
          <g id="building" transform="translate(600,300)  scale(1.5)">
            <!-- Base -->
            <rect x="-50" y="950" width="700" height="150" fill="#15202a" rx="25">
              <animate attributeName="opacity" values="0;1" dur="2s" fill="freeze"/>
            </rect>

            <!-- Main tower -->
            <g id="tower">
              <rect x="0" y="0" width="600" height="1100" fill="url(#glassGrad)" opacity="0.95" stroke="#0a1620" stroke-width="4">
                <animate attributeName="height" values="0;1100" dur="1s" fill="freeze"/>
                <animateTransform attributeName="transform" type="translate" additive="true" values="0,1100;0,0" dur="1s" fill="freeze"/>
              </rect>

              <!-- Windows -->
              <g opacity="0.85">
                <!-- Dynamic windows based on actual floor count -->
                <g v-for="(floor, index) in calculatedFloorPositions" :key="'windows-' + index">
                  <!-- Calculate Y position for this floor's windows -->
                  <g :transform="`translate(0, ${floor.windowY})`">
                    <!-- Five window columns for this floor -->
                    <rect x="50"  y="10" width="90" height="80" fill="url(#windowGrad)" rx="8"/>
                    <rect x="160" y="10" width="90" height="80" fill="url(#windowGrad)" rx="8"/>
                    <rect x="270" y="10" width="90" height="80" fill="url(#windowGrad)" rx="8"/>
                    <rect x="380" y="10" width="90" height="80" fill="url(#windowGrad)" rx="8"/>

                    <!-- Optional: glowing highlights on some windows randomly -->
                    <rect x="50"  y="10" width="90" height="80" fill="#00ddff" opacity="0.15" rx="8"
                          v-if="Math.random() > 0.7"/>
                    <rect x="160" y="10" width="90" height="80" fill="#00ddff" opacity="0.15" rx="8"
                          v-if="Math.random() > 0.7"/>
                    <rect x="380" y="10" width="90" height="80" fill="#00ddff" opacity="0.15" rx="8"
                          v-if="Math.random() > 0.7"/>
                  </g>
                </g>

                <!-- Keep the horizontal floor separator lines (optional but recommended) -->
                <g stroke="#00ddff" stroke-width="1.5" opacity="0.25">
                  <line v-for="(floor, index) in calculatedFloorPositions" 
                        :key="'separator-' + index"
                        x1="0" :y1="floor.separatorY" x2="600" :y2="floor.separatorY">
                    <animate attributeName="opacity" values="0.2;0.4;0.2" 
                            :begin="index * 0.8 + 's'" dur="6s" repeatCount="indefinite"/>
                  </line>
                </g>

                <!-- Highlight overlay -->
                <rect x="0" y="0" width="600" height="1100" fill="url(#highlightGrad)" opacity="0.2"/>
              </g>

              <!-- Side depth -->
              <polygon points="0,0 35,60 35,1040 0,1100" fill="#0f1f2f" opacity="0.8"/>
              <polygon points="600,0 565,60 565,1040 600,1100" fill="#0f1f2f" opacity="0.8"/>

              <!-- Roof -->
              <g id="roof">
                <rect x="-40" y="-80" width="680" height="100" fill="#1e2a38" rx="20"/>
                <rect x="20" y="-60" width="560" height="40" fill="#15202c" rx="10"/>
                <rect x="100" y="-110" width="120" height="50" fill="#0f1822" rx="8"/>
                <rect x="380" y="-110" width="120" height="50" fill="#0f1822" rx="8"/>
                <rect x="240" y="-100" width="120" height="40" fill="#0f1822" rx="6"/>
              </g>
            </g>
          </g>

          <!-- Entrance (in front) -->
          <g id="entrance" transform="translate(600,300)  scale(1.5)">
            <rect x="200" y="920" width="200" height="180" fill="#0a1218" rx="15">
              <animate attributeName="opacity" values="0;1" dur="2.5s" fill="freeze"/>
            </rect>

            <g id="leftDoor">
              <rect x="220" y="940" width="80" height="140" fill="#1e4055" rx="8" opacity="0.8"/>
              <rect x="220" y="940" width="80" height="140" fill="none" stroke="#00ddff" stroke-width="3" opacity="0.6"/>
              <animateTransform attributeName="transform" type="translate" values="0,0; -80,0; -80,0; 0,0" dur="8s" repeatCount="indefinite"/>
            </g>

            <g id="rightDoor">
              <rect x="300" y="940" width="80" height="140" fill="#1e4055" rx="8" opacity="0.8"/>
              <rect x="300" y="940" width="80" height="140" fill="none" stroke="#00ddff" stroke-width="3" opacity="0.6"/>
              <animateTransform attributeName="transform" type="translate" values="0,0; 80,0; 80,0; 0,0" dur="8s" repeatCount="indefinite"/>
            </g>

            <rect x="220" y="940" width="160" height="140" fill="url(#entranceGlow)" opacity="0.5">
              <animate attributeName="opacity" values="0.4;0.8;0.8;0.4" dur="8s" repeatCount="indefinite"/>
            </rect>

            <rect x="180" y="900" width="240" height="40" fill="#1e2a38" rx="12">
              <animate attributeName="opacity" values="0;1" dur="3s" fill="freeze"/>
            </rect>
            <rect x="190" y="910" width="220" height="20" fill="#0f1822"/>
          </g>

          <!-- Title -->
          <text x="100" y="180" fill="#e0e0e0" font-size="68" font-weight="bold" opacity="0">
            <animate attributeName="opacity" values="0;1" dur="2s" fill="freeze"/>
          </text>Building-view</text>
          <text x="100" y="250" fill="#88aaaa" font-size="38" opacity="0">
            <animate attributeName="opacity" values="0;1" begin="2.5s" dur="1.5s" fill="freeze"/>
          </text>Architectural Safety View</text>

          <!-- Subtle particles -->
          <g opacity="0.5">
            <circle cx="700" cy="600" r="2.5" fill="#00ddff">
              <animate attributeName="cy" values="600;300" dur="12s" repeatCount="indefinite"/>
              <animate attributeName="opacity" values="0;0.8;0" dur="12s" repeatCount="indefinite"/>
            </circle>
            <circle cx="1100" cy="800" r="2" fill="#4caf50">
              <animate attributeName="cy" values="800;500" dur="14s" repeatCount="indefinite"/>
              <animate attributeName="opacity" values="0;0.8;0" dur="14s" repeatCount="indefinite"/>
            </circle>
          </g>

          <!-- Dynamic Floor Cards Group -->
          <g id="floorCards"></g>
        </svg>
      </div>
    </b-card>
    <BuildingEmergencyModal
      :floor="floor"
      @modalClosed="onModalClosed"
      :key="`view-${buildingEmergencyModalCount}`"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import util from "@/util.js";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import BuildingEmergencyModal from "@/components/building/BuildingEmergencyModal.vue";



export default {
  components: {
    BuildingEmergencyModal,
  },
  data() {
    return {
      fields: [
        { key: "name", label: "Name" },
        { key: "phone_number", label: "Contact Number" },
        { key: "building_data", label: "Building" },
        { key: "is_peep", label: "PEEP Status" },
        { key: "is_active", label: "Response on Alert" },
        { key: "in_building", label: "In Building" },
        { key: "exit_building", label: "Exited From Building" },
        { key: "reached_assembly_area", label: "Reached Assembly Area" },
        { key: "updated_at", label: "Last Update At" },
      ],
      user_flats_fields: [
        { key: "nominee_name", label: "Nominee Name" },
        { key: "nominee_contact", label: "Nominee Contact" },
        { key: "assistance_type", label: "Assistance Type" },
      ],
      currentPage: 1,
      perPage: 0,
      totalItems: 0,
      building_emergency_data: [],
      building_emergency_data_list: [],
      searchTypes: [
        { value: 1, name: "Name" },
        { value: 2, name: "Building" },
        { value: 3, name: "Mobile Number" },
        { value: 4, name: "Peep Status" },
        { value: 5, name: "In Building" },
        { value: 6, name: "Reached Assembly Area" },
      ],
      searchType: null,
      buildings: [],
      name : "",
      building : "",
      mobile_number : "",
      is_peep : "",
      in_building : "",
      reached_assembly_area : "",
      floorData: {},
      buildingEmergencyModalCount: 0,
      floor: {},
      floorStatusInterval: null,
    };
  },
  mixins: [util],
  created() {
    this.searchType = this.searchTypes[0];
  },
  async mounted() {
    await this.fetchPaginatedData();
    await this.fetchUnPaginatedData();
    const sites = await this.getSitesDDL();
    this.sites = sites.data;

    const buildings = await this.getBuildingsDDL();
    this.buildings = buildings.data;

    const floors = await this.getFloorsDDL();
    this.floors = floors.data;
  },
  methods: {
    ...mapActions({
      getSitesDDL: "appData/getSitesDDL",
      getBuildingsDDL: "appData/getBuildingsActiveDDL",
      getFloorsDDL: "appData/getFloorsDDL",
      getBuildingEmergencyState: "appData/getBuildingEmergencyState",
      getFlatUsers: "appData/getFlatUsers",
      getFlatUsersList: "appData/getFlatUsersList",
      getBuildingFloorStatus: "appData/getBuildingFloorStatus",
    }),

    async refreshBuildingFloorStatus() {
      if (!this.building || !this.building.id) return;

      try {
        const building_data = await this.getBuildingFloorStatus({
          pk: this.building.id,
        });

        this.floorData = building_data.data;
        this.renderFloors();
      } catch (error) {
        this.displayError(error);
      }
    },
    
    async search() {
      // if (this.searchType) {
      //   switch (this.searchType.value) {
      //     case 1:
      //       this.building = "";
      //       this.mobile_number = "";
      //       this.is_peep = "";
      //       this.in_building = "";
      //       this.reached_assembly_area = "";
      //       break;
      //     case 2:
      //       this.name = "";
      //       this.mobile_number = "";
      //       this.is_peep = "";
      //       this.in_building = "";
      //       this.reached_assembly_area = "";
      //       break;
      //     case 3:
      //       this.name = "";
      //       this.building = "";
      //       this.is_peep = "";
      //       this.in_building = "";
      //       this.reached_assembly_area = "";
      //       break;
      //     case 4:
      //       this.name = "";
      //       this.building = "";
      //       this.mobile_number = "";
      //       this.in_building = "";
      //       this.reached_assembly_area = "";
      //       break;
      //     case 5:
      //       this.name = "";
      //       this.building = "";
      //       this.mobile_number = "";
      //       this.is_peep = "";
      //       this.reached_assembly_area = "";
      //       break;
      //     case 6:
      //       this.name = "";
      //       this.building = "";
      //       this.mobile_number = "";
      //       this.is_peep = "";
      //       this.in_building = "";
      //       break;
      //             }
      // } else {
      //   this.name = "";
      //   this.building = "";
      //   this.mobile_number = "";
      //   this.is_peep = "";
      //   this.in_building = "";
      //   this.reached_assembly_area = "";
      // }
      this.currentPage = 1;
      await this.fetchPaginatedData();
      await this.fetchUnPaginatedData();

      await this.refreshBuildingFloorStatus();

      if (this.floorStatusInterval) {
        clearInterval(this.floorStatusInterval);
      }

      this.floorStatusInterval = setInterval(() => {
        this.refreshBuildingFloorStatus();
      }, 8000);

    },
    resetSearch(){
        this.name = "";
        this.building = "";
        this.mobile_number = "";
        this.is_peep = "";
        this.in_building = "";
        this.reached_assembly_area = "";
    },
    async fetchPaginatedData() {
      try {
        const res = await this.getFlatUsers({
          pageNumber: this.currentPage,
          name : this.name,
          building : this.building.id,
          mobile_number : this.mobile_number,
          is_peep : this.is_peep.value,
          in_building : this.in_building.value,
          reached_assembly_area : this.reached_assembly_area.value,
        });
        this.building_emergency_data = res.data.results;
        this.totalItems = res.data.count;
        this.perPage = res.data.per_page;
      } catch (error) {
        this.displayError(error);
      }
    },

    viewBuildingEmergency() {
      this.buildingEmergencyModalCount += 1;
      this.$nextTick(() => {
        this.$bvModal.show("building-emergency-modal");
      });
    },

    async fetchUnPaginatedData() {
      try {
        const res = await this.getFlatUsersList({
          pageNumber: this.currentPage,
          name : this.name,
          building : this.building.id,
          mobile_number : this.mobile_number,
          is_peep : this.is_peep.value,
          in_building : this.in_building.value,
          reached_assembly_area : this.reached_assembly_area.value,
        });
        this.building_emergency_data_list = res.data;
      } catch (error) {
        this.displayError(error);
      }
    },

    badgeVariant(status) {
      switch (status) {
        case 'Approved':
          return 'success';
        case 'Rejected':
          return 'danger';
        case 'Pending':
          return 'warning';
        case 'Left':
          return 'dark';
        default:
          return 'light';
      }
    },
    badgeVariantUserFlats(status) {
      switch (status) {
        case true:
          return 'success';
        case false:
          return 'danger';
        case null:
          return 'warning';
        
        default:
          return 'light';
      }
    },
    
    rowClass(item, type) {
      if (!item || type !== 'row') return
      if (item.in_building === true  && (item.exit_building === false || item.exit_building === null) && (item.reached_assembly_area === false || item.reached_assembly_area === null)) return 'table-danger'
      if (item.in_building === true  && item.exit_building === true && (item.reached_assembly_area === false || item.reached_assembly_area === null)) return 'table-primary'
      if (item.in_building === true && item.reached_assembly_area === true) return 'table-success'
    },

    exportToCSV() {
      const headers = [
        'Name',
        'Contact Number',
        'Building',
        'PEEP Status',
        'Response on Alert',
        'In Building',
        'Exited From Building',
        'Reached Assembly Area',
        'Last Update At'
      ].join(',')
      
      const rows = this.building_emergency_data_list.map(item => {
        return [
          `"${item.user_data ? item.user_data.name : ''}"`,
          `"${item.user_data ? item.user_data.mobile_number : ''}"`,
          `"${item.building_data ? item.building_data.name : ''}"`,
          `"${item.user_data.is_peep ? 'Yes' : 'No'}"`,
          `"${item.is_active ? 'Yes' : 'No'}"`,
          `"${item.in_building ? 'Yes' : 'No'}"`,
          `"${item.exit_building ? 'Yes' : 'No'}"`,
          `"${item.reached_assembly_area ? 'Yes' : 'No'}"`,
          `"${item.updated_at}"`
        ].join(',')
      }).join('\n')
      
      const csvContent = `${headers}\n${rows}`
      
      // Create download link
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      const url = URL.createObjectURL(blob)
      
      link.setAttribute('href', url)
      link.setAttribute('download', 'emergency_status_report.csv')
      link.style.visibility = 'hidden'
      
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    },

    calculatePositions() {
      const totalFloors = this.floorData.floors.length;
      const startY = 250;
      const endY = 1800;
      const spacing = (endY - startY) / (totalFloors - 1);

      return this.floorData.floors.slice().reverse().map((floor, index) => {
        const cardY = startY + index * spacing;
        const lineY = cardY + 90;
        const isRightSide = index % 2 === 0;
        return { ...floor, cardY, lineY, isRightSide };
      });
    },
    renderFloors() {
      const svg = this.$refs.svg;
      if (!svg) return;

      const floorCardsGroup = svg.querySelector('#floorCards');
      if (!floorCardsGroup) return;

      const positions = this.calculatePositions();

      /////////////////////////////////////////////
        // Clear previous content
      floorCardsGroup.innerHTML = '';

      // === FULL-WIDTH WINDOW PER FLOOR - EQUAL FLOOR HEIGHTS ===
      const tower = svg.querySelector('#tower');
      const windowsParent = tower.querySelector('g[opacity="0.85"]');
      if (windowsParent) {
        // Keep only the highlight overlay
        windowsParent.innerHTML = '<rect x="0" y="0" width="600" height="1100" fill="url(#highlightGrad)" opacity="0.2"/>';
      } else {
        return;
      }

      const floorCount = this.floorData.floors ? this.floorData.floors.length : 10;
      if (floorCount === 0) return;

      // Use the FULL tower height (from y=0 to y=1100 inside the tower rect)
      const totalTowerHeight = 1100;
      const floorHeight = totalTowerHeight / floorCount;

      // Window takes ~60% of floor height, centered vertically
      const windowHeight = floorHeight * 0.80;
      const verticalPadding = (floorHeight - windowHeight) / 2;

      // Reverse floors to match visual order (top floor first)
      const reversedFloors = this.floorData.floors.slice().reverse();

      // Render one full-width window bar per floor
      for (let i = 0; i < floorCount; i++) {
        const yBase = i * floorHeight + verticalPadding;

        const windowGroup = document.createElementNS("http://www.w3.org/2000/svg", 'g');
        windowGroup.setAttribute('transform', `translate(0, ${yBase})`);

        // Main full-width dark window bar
        const win = document.createElementNS("http://www.w3.org/2000/svg", 'rect');
        win.setAttribute('x', 0);
        win.setAttribute('y', 0);
        win.setAttribute('width', 600);
        win.setAttribute('height', windowHeight);
        win.setAttribute('rx', 8);
        win.setAttribute('fill', 'url(#windowGrad)');
        windowGroup.appendChild(win);

        // Glow for floors with occupants
        const floor = reversedFloors[i];
        const safePeople = Number(floor.safe) === Number(floor.visitors) + Number(floor.residents) + Number(floor.peeps);

        if (!safePeople) {
          const glow = document.createElementNS("http://www.w3.org/2000/svg", 'rect');
          glow.setAttribute('x', 0);
          glow.setAttribute('y', 0);
          glow.setAttribute('width', 600);
          glow.setAttribute('height', windowHeight);
          glow.setAttribute('rx', 8);
          glow.setAttribute('fill', '#ff0000');
          glow.setAttribute('opacity', '0.35');

          const animate = document.createElementNS("http://www.w3.org/2000/svg", 'animate');
          animate.setAttribute('attributeName', 'opacity');
          animate.setAttribute('values', '0.25;0.5;0.25');
          animate.setAttribute('dur', '4s');
          animate.setAttribute('repeatCount', 'indefinite');
          glow.appendChild(animate);

          windowGroup.appendChild(glow);
        }

        windowsParent.insertBefore(windowGroup, windowsParent.firstChild);
      }

      // Subtle glowing floor dividers
      const separators = document.createElementNS("http://www.w3.org/2000/svg", 'g');
      separators.setAttribute('stroke', '#00ddff');
      separators.setAttribute('stroke-width', '1.5');
      separators.setAttribute('opacity', '0.25');

      for (let i = 1; i < floorCount; i++) {
        const lineY = i * floorHeight;
        const line = document.createElementNS("http://www.w3.org/2000/svg", 'line');
        line.setAttribute('x1', 0);
        line.setAttribute('y1', lineY);
        line.setAttribute('x2', 600);
        line.setAttribute('y2', lineY);

        const anim = document.createElementNS("http://www.w3.org/2000/svg", 'animate');
        anim.setAttribute('attributeName', 'opacity');
        anim.setAttribute('values', '0.2;0.4;0.2');
        anim.setAttribute('begin', `${i * 0.5}s`);
        anim.setAttribute('dur', '6s');
        anim.setAttribute('repeatCount', 'indefinite');
        line.appendChild(anim);

        separators.appendChild(line);
      }
      windowsParent.insertBefore(separators, windowsParent.firstChild);
      // === END EQUAL FLOOR HEIGHTS ===

      ////////////////////////////////////////////

      // Lines group (first)
      const linesGroup = document.createElementNS("http://www.w3.org/2000/svg", 'g');
      floorCardsGroup.appendChild(linesGroup);

      // Cards group (after lines)
      const cardsGroup = document.createElementNS("http://www.w3.org/2000/svg", 'g');
      cardsGroup.setAttribute('font-family', "'Segoe UI', Arial, sans-serif");
      floorCardsGroup.appendChild(cardsGroup);

      positions.forEach((floor, index) => {
        const id = `label${index + 1}`;

        // Building center X
        const buildingCenterX = 1100;

        const lineStartX = floor.isRightSide ? buildingCenterX + 50 : buildingCenterX - 50;
        const lineEndX = floor.isRightSide ? 1700 : 100;

        // Line
        const line = document.createElementNS("http://www.w3.org/2000/svg", 'line');
        line.setAttribute('x1', lineStartX);
        line.setAttribute('y1', floor.lineY);
        line.setAttribute('x2', lineEndX);
        line.setAttribute('y2', floor.lineY);
        line.setAttribute('stroke', '#666');
        line.setAttribute('stroke-width', '3');
        line.setAttribute('stroke-dasharray', '8,6');

        const lineAnim = document.createElementNS("http://www.w3.org/2000/svg", 'animate');
        lineAnim.setAttribute('attributeName', 'stroke-dashoffset');
        lineAnim.setAttribute('values', '0;14;0');
        lineAnim.setAttribute('dur', '3s');
        lineAnim.setAttribute('repeatCount', 'indefinite');
        line.appendChild(lineAnim);
        linesGroup.appendChild(line);

        // Card
        const cardX = floor.isRightSide ? 1700 : -200;
        const cardWidth = 600;

        const g = document.createElementNS("http://www.w3.org/2000/svg", 'g');


        g.style.cursor = 'pointer';
        g.style.transition = 'transform 1s ease';
        g.style.transformBox = 'fill-box';
        g.style.transformOrigin = 'center center';

        g.addEventListener('mouseenter', () => {
          g.style.transform = 'scale(2)';
          g.parentNode.appendChild(g);
        });

        g.addEventListener('mouseleave', () => {
          g.style.transform = 'scale(1)';
        });

        g.addEventListener('click', () => {
          this.floor = { ...floor };
          this.viewBuildingEmergency();
        });


        g.setAttribute('id', id);
        cardsGroup.appendChild(g);

        let background_color = '';

        if (
          Number(floor.safe) ===
          Number(floor.visitors) + Number(floor.residents) + Number(floor.peeps)
        ) {
          background_color = '#229400';
        }
        else if (Number(floor.in_building) > 0) {
          background_color = '#F52727';
        }
        else if(Number(floor.exit_building) > 0)
        {
          background_color = '#ffbf00';
        }
        else{
          background_color = '#ffbf00';
        }
        // Background
        const rect = document.createElementNS("http://www.w3.org/2000/svg", 'rect');
        rect.setAttribute('x', cardX);
        rect.setAttribute('y', floor.cardY);
        rect.setAttribute('width', cardWidth);
        rect.setAttribute('height', 230);
        rect.setAttribute('rx', 18);
        rect.setAttribute('fill', `${background_color}`);
        rect.setAttribute('stroke', 'black');
        rect.setAttribute('stroke-width', 2);
        rect.setAttribute('opacity', 0);
        rect.setAttribute('filter', 'url(#labelShadow)');
        g.appendChild(rect);

        // Texts
        this.addText(g, cardX + 30, floor.cardY + 50, 'white', 30, floor.floor, 0);
        this.addText(g, cardX + 30, floor.cardY + 100, 'white', 36, `Residents: ${floor.residents.toLocaleString()}`, 0, 'bold');
        this.addText(g, cardX + 30, floor.cardY + 150, 'white', 30, `PEEPs: ${floor.peeps.toLocaleString()}`, 0);
        this.addText(g, cardX + 200, floor.cardY + 150, 'white', 30, `Visitors: ${floor.visitors}`, 0);
        this.addText(g, cardX + 390, floor.cardY + 150, 'white', 30, `In Building: ${floor.in_building}`, 0);
        this.addText(g, cardX + 30, floor.cardY + 190, 'white', 30, `Exited Building: ${floor.exit_building}`, 0);
        this.addText(g, cardX + 290, floor.cardY + 190, 'white', 30, `Reached Assembly: ${floor.reached_assembly_area}`, 0);
      });

      // Sequential fade-in
      positions.forEach((floor, index) => {
        setTimeout(() => {
          const group = svg.getElementById(`label${index + 1}`);
          if (group) {
            group.querySelectorAll('rect, text').forEach(el => {
              el.setAttribute('opacity', '1');
            });
          }
        }, 1000 + index * 100);
      });
    },
    addText(parent, x, y, fill, fontSize, text, opacity, weight = 'normal') {
      const textEl = document.createElementNS("http://www.w3.org/2000/svg", 'text');
      textEl.setAttribute('x', x);
      textEl.setAttribute('y', y);
      textEl.setAttribute('fill', fill);
      textEl.setAttribute('font-size', fontSize);
      if (weight === 'bold') textEl.setAttribute('font-weight', 'bold');
      textEl.setAttribute('opacity', opacity);
      textEl.textContent = text;
      parent.appendChild(textEl);
    }

  },
  beforeDestroy() {
    if (this.floorStatusInterval) {
      clearInterval(this.floorStatusInterval);
    }
  },
  computed: {
    ...mapGetters({ hasPermission: "appData/hasPermission", getLoggedInUser: "appData/getLoggedInUser" },
    ),
  },
  watch: {
    currentPage: async function (val) {
      await this.fetchPaginatedData();
      await this.fetchUnPaginatedData();
    },
  },
};
</script>

<style scoped>
.amber-background {
  background-color: #ffbf00 !important;
}
.svg-container {
  width: 100%;
  height: 75vh;
  overflow: hidden;
  background: white;
  display: flex;
  justify-content: center;
  align-items: center;
}

#buildingSvg {
  width: 100%;
  height: 100%;
  max-height: 100vh;
}
.amber-background {
  background-color: #ffbf00 !important;
}

</style>

